import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HIDDEN, SHARED } from '@careapp-group/core';
import { ChatDto } from '@careapp-group/ng-sdk';
import { Actions, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, tap, throttleTime } from 'rxjs/operators';
import {
  Announcements,
  CAAction,
  Chats,
  CheckIns,
  CheckInTags,
  Conversations,
  CustomerFiles,
  Feedback,
  Memoriams,
  Memos,
  Requests,
  ResponseSuccessChangePasswordAction,
  RESPONSE_FAIL_INVITATION_SEND,
  SHOW_SNACKBAR,
  Tenants,
  CustomerLists,
  SuccessUpdateSettingsFlow,
  MemberBadgesActions,
} from '../actions';
import {
  Customers,
  CustomerTeam,
  FORCE_LOGOUT_401,
  MemoCategories,
  Organisations,
  RESPONSE_FAIL_LOCATION,
  RESPONSE_FILE_UPLOAD_ERROR,
  RESPONSE_SUCCESS_INVITATION_CANCEL,
  RESPONSE_SUCCESS_INVITATION_RESEND,
  RESPONSE_SUCCESS_INVITATION_SEND,
  Members,
} from '../actions/index';
import {
  FAIL_TOKEN_REFRESH,
  NO_ORGANISATIONS,
  NO_ROLES,
  NO_USER,
} from '../core/core-kratos.effects';
import * as Profile from '../profile/profile.actions';
import { Questions } from '../questions/questions.actions';
import { SnackBarData } from './snackbar-data';
import { WebhooksActions } from '../../integrations/store/webhooks/webhooks.actions';
import { OauthClientsActions } from '../../integrations/store/oauth-clients/oauth-clients.actions';
import { StreamAction } from '../stream/stream.actions';
import { Enquiries } from '../../enquiries/store/enquiries.actions';
import { MarketFrontEndOrders } from '../../market/store/market-front-end-order/market-front-end-order.actions';
import { MarketManagementOrder } from '../../market/store/market-management-order/market-management-order.actions';
import { MarketFrontEndSubscriptions } from '../../market/store/market-front-end-subscription/market-front-end-subscription.actions';

@Injectable()
/**
 * Effects class for deciding whether to show a snackbar when an action occurs
 */
export class SnackBarEffects {
  snacks = createEffect(
    () =>
      this.actions$.pipe(
        tap((action) => {
          const data: SnackBarData = this.getSnackbarDataForAction(action);
          if (!!data) {
            return this.displaySnackbar(data);
          }
        }),
      ),
    { dispatch: false },
  );

  disabledWarn = createEffect(
    () =>
      this.actions$.pipe(
        filter((action) => action.type.includes('[Fail]')),
        filter(
          (action) =>
            action.payload && action.payload.code && action.payload.code.includes('_DISABLED'),
        ),
        throttleTime(3000),
        tap((action) => {
          switch (action.payload.code) {
            case 'ACCOUNT_DISABLED':
              return this.displaySnackbar(
                new SnackBarData(
                  'Your account appears to be disabled. Speak to an administrator from your site',
                ),
              );
            case 'ORGANISATION_DISABLED':
              return this.displaySnackbar(
                new SnackBarData(
                  'This site appears to be disabled. Speak to a CareApp administrator',
                ),
              );
          }
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private snackbar: MatSnackBar,
  ) {}

  displaySnackbar(data: SnackBarData) {
    if (!data.message) {
      return;
    } else if (!data.hasButton) {
      this.snackbar.open(data.message, null, { duration: data.duration });
    } else {
      const snackBarRef = this.snackbar.open(data.message, data.buttonText, {
        duration: data.duration,
      });
      snackBarRef.onAction().subscribe(() => {
        data.buttonCallback();
      });
    }
  }

  getSnackbarDataForAction(action): SnackBarData {
    if (
      action.payload &&
      (action.payload.code === 'ACCOUNT_DISABLED' ||
        action.payload.code === 'ORGANISATION_DISABLED')
    ) {
      return null;
    }

    switch (action.type) {
      case SHOW_SNACKBAR:
        // Should already be a SnackBarData instance
        return action?.payload;
      case Announcements.RESPONSE_SUCCESS_CREATE:
        return new SnackBarData(`Notice posted`);
      case Announcements.RESPONSE_SUCCESS_SAVE:
        return new SnackBarData(`Notice saved`);
      case Announcements.RESPONSE_SUCCESS_DESTROY_BY_ID:
        return new SnackBarData(`Notice deleted`);
      case Announcements.RESPONSE_FAIL_TOTAL_COUNT:
        return new SnackBarData(`Unable to fetch Notice count`);
      case Chats.RESPONSE_SUCCESS_CREATE:
        return new SnackBarData(`Created chat`);
      case Chats.RESPONSE_SUCCESS_END:
        return new SnackBarData(`Chat ended`);
      case Chats.RESPONSE_FAIL_JOIN:
        if (action.payload && action.payload.code === 'CHAT_ENDED') {
          return new SnackBarData(`This chat has ended`);
        } else {
          return new SnackBarData(`Unable to join chat`);
        }
      case Chats.RESPONSE_FAIL_END:
        return new SnackBarData(`Unable to end chat`);
      case Chats.RESPONSE_FAIL_ALL:
        return new SnackBarData(`Could not fetch active chats`);
      case Chats.NOTIFY_NEW_CHAT_AVAILABLE:
        const response: ChatDto = action.chat;
        if (response != null) {
          return new SnackBarData(`You have been invited to video chat`, `Join`, () => {
            this.store.dispatch(Chats.JoinNow({ chatId: response.id }));
          });
        } else {
          return new SnackBarData(`A new video chat is available`);
        }

      case CheckIns.ResponseSuccessCreateAction.type:
        return new SnackBarData(`Check in created`);
      case CheckIns.ResponseFailCreateAction.type:
        return new SnackBarData(`Failed to check in`);
      case CheckInTags.RESPONSE_SUCCESS_SAVE:
        return new SnackBarData(`Tag updated`);
      case CheckInTags.RESPONSE_FAIL_SAVE:
        return new SnackBarData(`Unable to update tag`);
      case CheckInTags.RESPONSE_SUCCESS_CREATE:
        return new SnackBarData(`New tag created`);
      case CheckInTags.RESPONSE_FAIL_CREATE:
        return new SnackBarData(`Failed to create tag`);
      case Memoriams.RESPONSE_SUCCESS_CREATE:
        return new SnackBarData(`Memoriam created`);
      case Memoriams.RESPONSE_FAIL_CREATE:
        return new SnackBarData(`Failed to create memoriam`);
      case Memoriams.RESPONSE_SUCCESS_COMPLETE:
        return new SnackBarData(`Memoriam complete`);
      case Memoriams.RESPONSE_FAIL_COMPLETE:
        return new SnackBarData(`Failed to complete memoriam`);
      case Memoriams.RESPONSE_SUCCESS_REMOVE:
        return new SnackBarData(`Slideshow discarded`);
      case Memoriams.RESPONSE_FAIL_REMOVE:
        return new SnackBarData(`Failed to discard Slideshow`);
      case Memos.RESPONSE_SUCCESS_ADD_COMMENT:
        return new SnackBarData(`Comment posted`);
      case Memos.RESPONSE_FAIL_CREATE_ACTIONED: {
        if (action.payload.error && action.payload.error.message) {
          return new SnackBarData(`Error: ` + action.payload.error.message);
        } else {
          return new SnackBarData(`Request failed for an unknown reason.`);
        }
      }
      case Memos.responseSuccessCreateActionedAction.type: {
        const typedAction: ReturnType<typeof Memos.responseSuccessCreateActionedAction> = action;

        let type = 'Item';

        switch (typedAction.memo.type) {
          case 'note':
            type = 'Note';
            break;
          case 'gallery':
            type = 'Photo';
            break;
          case 'video':
            type = 'Video';
            break;
        }
        switch (typedAction.actionType) {
          case HIDDEN:
            return new SnackBarData(`${type} hidden`);
          case SHARED:
            return new SnackBarData(`${type} shared`);
        }
      }
      case Memos.RESPONSE_FAIL_ADD_COMMENT:
        return new SnackBarData(`Failed to post comment`);
      case Memos.RESPONSE_SUCCESS_CREATE: {
        let type = 'Item';
        switch (action.payload.type) {
          case 'note':
            type = 'Note';
            break;
          case 'gallery':
            type = 'Photo';
            break;
          case 'video':
            type = 'Video';
            break;
        }
        return new SnackBarData(`${type} posted!`);
      }
      case Memos.RESPONSE_FAIL_CREATE:
        return new SnackBarData(`Failed to submit`);
      case Feedback.ResponseSuccessCreateAction.type:
        return new SnackBarData('Feedback Posted');
      case Requests.RESPONSE_SUCCESS_RESPOND:
        return new SnackBarData(`Response sent`);
      case Requests.RESPONSE_FAIL_RESPOND:
        return new SnackBarData(`Failed to send response`);
      case ResponseSuccessChangePasswordAction.type:
        return new SnackBarData(`Password changed`);
      case Profile.ResponseSuccessSaveProfile.type:
        return new SnackBarData(`Profile updated`);
      case Profile.ResponseFailSaveProfile.type:
        return new SnackBarData(`Failed to save profile at this time`);
      case Organisations.SUCCESS_UPDATE_PREFERENCES:
        return new SnackBarData(`Preferences saved`);
      case CustomerTeam.RESPONSE_SUCCESS_CREATE:
        return new SnackBarData(`Added to team`);
      case Customers.ResponseSuccessAssignCustomerFile.type:
        return new SnackBarData(`Document added`);
      case CustomerFiles.RESPONSE_SUCCESS_DESTROY_BY_ID:
        return new SnackBarData(`Document removed`);
      case CustomerFiles.RESPONSE_SUCCESS_SAVE:
        return new SnackBarData(`Document updated`);
      case CustomerFiles.RESPONSE_FAIL_DESTROY_BY_ID:
        return new SnackBarData(`Failed to remove document`);
      case CustomerFiles.RESPONSE_FAIL_SAVE:
        return new SnackBarData(`Failed to update document`);
      case Customers.ResponseFailAssignCustomerFile.type:
        return new SnackBarData(`Could not add document at this time`);
      case Customers.ResponseSuccessAddLink.type:
        return new SnackBarData(`Link added`);
      case Customers.ResponseFailAddLink.type:
        return new SnackBarData(`Could not add link at this time`);
      case Customers.RESPONSE_FAIL_BULK_ADD_CUSTOMERS:
      case Members.RESPONSE_FAIL_BULK_IMPORT:
        return new SnackBarData('Failed to send upload request to server');
      case Customers.RESPONSE_SUCCESS_CREATE:
        return new SnackBarData('Customer Created');
      case RESPONSE_FILE_UPLOAD_ERROR: {
        if (action.payload && action.payload.status === 415) {
          return new SnackBarData(
            'Upload failed - that file type is not currently supported by CareApp',
          );
        }
        if (action.payload && action.payload.status === 413) {
          return new SnackBarData('Upload failed - the file was too large');
        }
        if (action.payload && action.payload.status === 0) {
          return new SnackBarData('Upload failed due to network error');
        }
        return new SnackBarData('File upload failed for an unknown reason');
      }
      case Organisations.NO_VALID_ORGANISATIONS:
        return new SnackBarData(
          'You do not have significant permissions to view any organisations',
        );
      case Organisations.RESPONSE_SUCCESS_CREATE:
        return new SnackBarData(`Organisation Created`);
      case Members.RESPONSE_SUCCESS_CREATE:
        return new SnackBarData(`Staff Member Created`);
      case Members.RESPONSE_SUCCESS_UPDATE_ROLE:
      case Members.ResponseSuccessUpdatePermission.type:
        return new SnackBarData(`Member Updated`);
      case Members.ResponseFailUpdatePermission.type:
      case Members.RESPONSE_FAIL_UPDATE_ROLE:
        return new SnackBarData(`Could not update staff permissions at this time`);
      case MemberBadgesActions.ResponseSuccessRemoveBadgeFromPermission.type:
      case MemberBadgesActions.ResponseSuccessAddBadgeToPermission.type:
        return new SnackBarData(`Badges updated`);
      case Organisations.SUCCESS_UPDATE_BRANDING_COLORS:
        return new SnackBarData(`Branding colours have been saved`);
      case Organisations.SUCCESS_UPDATE_BRANDING_IMAGE:
        return new SnackBarData(`New branding image has been saved`);
      case Organisations.FAIL_UPDATE_BRANDING_COLORS:
        return new SnackBarData(`Branding colours could not update at this time`);
      case Organisations.FAIL_UPDATE_BRANDING_IMAGE:
        return new SnackBarData(`Branding image could not update at this time`);
      case Organisations.RESPONSE_SUCCESS_SAVE:
        return new SnackBarData(`Organisation Saved`);
      case Customers.RESPONSE_SUCCESS_SAVE:
        return new SnackBarData(`Customer Saved`);
      case RESPONSE_SUCCESS_INVITATION_SEND:
        return new SnackBarData(`User Invited`);
      case RESPONSE_FAIL_INVITATION_SEND:
        if (action.payload && action.payload.statusCode === 409) {
          return new SnackBarData(
            'Could not invite user, as someone is already linked to this customer',
          );
        }
        return new SnackBarData('Could not invite user');

      case RESPONSE_SUCCESS_INVITATION_RESEND:
        return new SnackBarData(`Invitation Resent`);
      case RESPONSE_SUCCESS_INVITATION_CANCEL:
        return new SnackBarData(`Invitation cancelled`);
      case RESPONSE_FAIL_LOCATION:
        return new SnackBarData(`Could not find location`);
      case Customers.RESPONSE_FAIL_ALL:
      case Organisations.RESPONSE_FAIL_ALL:
      case Members.RESPONSE_FAIL_ALL:
        return new SnackBarData(`Could not load list. Please try again later`);
      case Profile.RESPONSE_SUCCESS_VERIFICATION:
        return new SnackBarData(
          `Verification code has been sent to: ${action.payload.to} via ${action.payload.channel}.`,
        );
      case Customers.RESPONSE_SUCCESS_ACCESS:
        return new SnackBarData('Request for access has been sent');
      case Customers.RESPONSE_SUCCESS_BULK_ADD_CUSTOMERS:
        return new SnackBarData(`Customer import complete`);
      case Customers.RESPONSE_FAIL_BY_ID:
      case Organisations.RESPONSE_FAIL_BY_ID:
      case Members.RESPONSE_FAIL_BY_ID:
        return new SnackBarData(`Could not load that item. Please try again later`);

      case CustomerTeam.RESPONSE_FAIL_CREATE:
        return new SnackBarData(`Failed to add to team`);
      case CustomerTeam.ResponseSuccessRemove.type:
        return new SnackBarData(`Removed from team`);
      case MemoCategories.RESPONSE_SUCCESS_CREATE:
        return new SnackBarData(`New category created!`);
      case MemoCategories.RESPONSE_FAIL_CREATE:
        return new SnackBarData(`Failed to create category`);
      case MemoCategories.RESPONSE_SUCCESS_SAVE:
        return new SnackBarData(`Category updated for all sites`);
      case MemoCategories.RESPONSE_SUCCESS_REMOVE_BY_ID:
        return new SnackBarData(`Category removed from site.`);
      case MemoCategories.RESPONSE_FAIL_REMOVE_BY_ID:
        return new SnackBarData(`Could not remove category from site. Please try again later.`);
      case MemoCategories.RESPONSE_SUCCESS_ADD_BY_ID:
        return new SnackBarData(`Category has been updated for this site.`);
      case MemoCategories.RESPONSE_FAIL_ADD_BY_ID:
        return new SnackBarData(`Could not add category from site. Please try again later.`);
      case Questions.RESPONSE_SUCCESS_CREATE:
        return new SnackBarData(`New question created!`);
      case Questions.RESPONSE_FAIL_CREATE:
        return new SnackBarData(`Failed to create question`);
      case Questions.RESPONSE_SUCCESS_ANSWER_QUESTION:
        return new SnackBarData(`Thank you for your feedback!`);
      case Questions.RESPONSE_FAIL_ANSWER_QUESTION:
        return new SnackBarData(`Failed to answer question`);

      // Conversations
      case Conversations.responseFailStartConversation.type:
        return new SnackBarData(`Failed to start conversation`);
      case StreamAction.responseFailInitStream.type:
        return new SnackBarData(`An issue occurred when initialising conversations`);
      case Conversations.responseSuccessStartConversation.type:
        return new SnackBarData(`Conversation started`);
      case Conversations.responseFailAddToChannel.type:
        return new SnackBarData(`Could not add to channel`);
      case Conversations.responseSuccessAddToChannel.type:
        return new SnackBarData(`Successfully added to channel`);

      // Memos
      case Memos.responseSuccessReportMemoComment.type:
        return new SnackBarData(`Comment reported`);
      case Memos.responseFailReportMemoComment.type:
        return new SnackBarData(`Failed to report comment`);
      case Memos.responseSuccessDeleteMemoComment.type:
        return new SnackBarData(`Comment removed`);
      case Memos.responseFailDeleteMemoComment.type:
        return new SnackBarData(`Failed to remove comment`);
      case Memos.responseSuccessDeleteReportsForComment.type:
        return new SnackBarData(`Cleared comment reports`);
      case Memos.responseFailDeleteReportsForComment.type:
        return new SnackBarData(`Failed to clear comment reports`);
      case Memos.responseSuccessApproveMemoComment.type:
        return new SnackBarData(`Comment approved`);
      case Memos.responseFailApproveMemoComment.type:
        return new SnackBarData(`Failed to approve comment`);

      // Customer lists
      case CustomerLists.RESPONSE_SUCCESS_PATCH_BY_ID:
        return new SnackBarData(`Customer list updated`);
      case CustomerLists.RESPONSE_FAIL_PATCH_BY_ID:
        return new SnackBarData(`Failed to update customer list`);
      case CustomerLists.RESPONSE_SUCCESS_DESTROY_BY_ID:
        return new SnackBarData(`Customer list removed`);
      case CustomerLists.RESPONSE_FAIL_DESTROY_BY_ID:
        return new SnackBarData(`Failed to remove customer list`);
      case CustomerLists.RESPONSE_SUCCESS_CREATE:
        return new SnackBarData(`Customer list created`);
      case CustomerLists.RESPONSE_FAIL_CREATE:
        return new SnackBarData(`Failed to create customer list`);

      case SuccessUpdateSettingsFlow.type:
        return new SnackBarData('Settings Updated');
      // Tenants
      case Tenants.ResponseFailCreateAction.type:
        return new SnackBarData(`Failed to create tenant`);
      case Tenants.ResponseSuccessCreateAction.type:
        return new SnackBarData(`Tenant created`);

      case OauthClientsActions.ResponseSuccessCreateAction.type:
        return new SnackBarData(`OAuth Client Created`);
      case OauthClientsActions.ResponseSuccessDeleteByIdAction.type:
        return new SnackBarData(`OAuth Client Deleted`);
      case WebhooksActions.ResponseSuccessCreateAction.type:
        return new SnackBarData(`Webhook created`);
      case WebhooksActions.ResponseSuccessDeleteByIdAction.type:
        return new SnackBarData(`Webhook deleted`);

      case Members.ResponseSuccessConvertToTenantAdmin.type:
        return new SnackBarData(`Member converted to Provider Administrator`);
      case Members.ResponseFailConvertToTenantAdmin.type:
        return new SnackBarData(`Failed to convert member to Provider Administrator`);

      // Enquiries
      case Enquiries.successUpdateEnquiryAssignee.type:
        return new SnackBarData(`Member assigned`);
      case Enquiries.failUpdateEnquiryAssignee.type:
        return new SnackBarData(`Could not assign at this time`);
      case Enquiries.successRateEnquiry.type:
        return new SnackBarData(`Thank you for your feedback!`);
      case Enquiries.failRateEnquiry.type:
        return new SnackBarData(`Could not rate at this time`);

      // Marking a Market Order as complete
      case MarketManagementOrder.ResponseSuccessCompleteOrderAction.type:
        return new SnackBarData(`Order marked as complete`);
      case MarketManagementOrder.ResponseFailCompleteOrderAction.type:
        return new SnackBarData(`Could not mark order as complete at this time`);

      // Could not purchase a product (out of stock)
      case MarketFrontEndOrders.beginPurchaseFlowFailureAction.type:
        const errorText = action.error.error.message ?? '';
        if (errorText.includes('stock')) {
          return new SnackBarData(`Please check stock and try again`);
        } else {
          return new SnackBarData(`Could not purchase product at this time`);
        }

      // Cancelling a subscription
      case MarketFrontEndSubscriptions.ResponseSuccessCancelSubscriptionAction.type:
        return new SnackBarData(`Subscription cancelled`);
      case MarketFrontEndSubscriptions.ResponseFailCancelSubscriptionAction.type:
        return new SnackBarData(`Could not cancel subscription at this time`);

      // Reviewing a product
      case MarketFrontEndOrders.reviewProductSuccessAction.type:
        return new SnackBarData(`Thank you for your review!`);
      case MarketFrontEndOrders.reviewProductFailureAction.type:
        return new SnackBarData(`Failed to post review. Please try again later.`);

      case MarketFrontEndOrders.respondToReviewSuccessAction.type:
        return new SnackBarData(`Response posted`);
      case MarketFrontEndOrders.respondToReviewFailureAction.type:
        return new SnackBarData(`Failed to post response`);
      case FORCE_LOGOUT_401: {
        switch (action.payload) {
          case NO_ORGANISATIONS:
            return new SnackBarData(
              'You do not seem to have sufficient permissions for any organisations. Please speak to an administrator.',
            );
          case NO_USER:
            return new SnackBarData(
              'You have been logged out because your user information could not be retrieved',
            );
          case NO_ROLES:
            return new SnackBarData(
              'You have been logged out because your role information could not be retrieved',
            );
          case FAIL_TOKEN_REFRESH:
            return new SnackBarData(
              'You have been logged out - this might be because of an extended period of innactivity.',
            );
          default:
            return new SnackBarData(`You have been automatically logged out.`);
        }
      }
    }
    return null;
  }
}
